import {
  Button,
  Grid,
  Input,
  InputWrapperProps,
  Modal,
  PasswordInput,
  Text,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import {
  IconDeviceFloppy,
  IconHistory,
  IconLock,
  IconLockOpen,
  IconX,
} from "@tabler/icons-react";
import { type FormEvent, Fragment, useCallback, useState } from "react";
import ApiHistoryModal from "src/components/shared/ApiHistoryModal/ApiHistoryModal";
import {
  ButtonRow,
  FormsContainer,
  Row,
  RowsContainer,
} from "src/generator/_Modals/AuthorizationModal/AuthorizationModal.styled";
import PinInput from "src/generator/PinInput/PinInput";
import { useCheckPerm, useGetApi, usePostApi } from "src/hooks";
import { type IProtectedUserData } from "src/pages/Settings/CrewSetUp/UserForm/UserForm.types";
import { SmallDivider } from "src/styled";
import { InputStyledWrapper } from "src/styled/UserForm.styled";
import { toMantineDateString } from "src/utils/mantineDates";
import { Notification } from "src/utils/Notification";

interface Props {
  userId: string;
}

const InputWrapper = (props: InputWrapperProps) => {
  return (
    <InputStyledWrapper style={{ width: "unset" }} {...props}>
      {props.children}
    </InputStyledWrapper>
  );
};

const Col = (props: any) => {
  return (
    <Grid.Col {...props}>
      <InputWrapper>{props.children}</InputWrapper>
    </Grid.Col>
  );
};

const ProtectedInfo = ({ userId }: Props) => {
  const checkPerm = useCheckPerm();
  const disabled = !checkPerm("editeuser");

  const [opened, { open, close }] = useDisclosure();
  const [historyOpened, { open: openHistory, close: closeHistory }] =
    useDisclosure();
  const [viewOpened, { open: openView, close: closeView }] = useDisclosure();
  const [pass, setPass] = useState("");
  const [pin, setPin] = useState("");
  const [token, setToken] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [data, setData] = useState<IProtectedUserData>({});
  const [viewData, setViewData] = useState<IProtectedUserData>({});

  const { post: authPost, isLoading: isAuthLoading } = usePostApi();
  const { refetch: refetchProtectedData } = useGetApi<IProtectedUserData>({
    request: "get-protected-user-data",
    body: { params: { userid: userId } },
    mapper: result => result?.data ?? {},
    fetchOnInit: false,
  });
  const { refetch: refetchProtectedDataHistItem } = useGetApi<{
    data: IProtectedUserData;
  }>({
    request: "view-protected-user-data-hist",
    mapper: result => result?.data ?? {},
    fetchOnInit: false,
  });
  const { post: savePost, isLoading: isSaveLoading } = usePostApi();

  const relock = useCallback(() => {
    setUnlocked(false);
    setToken("");
    setData({});
  }, []);

  const fetchProtectedData = useCallback(
    async (authToken: string) => {
      const result = await refetchProtectedData(
        { params: { userid: userId, token: authToken } },
        true,
      );
      if (!result) {
        relock();
        return;
      }
      setData({
        dob: result.dob ?? "",
        address: result.address ?? "",
        nextOfKin: result.nextOfKin ?? "",
      });
      setUnlocked(true);
    },
    [refetchProtectedData, relock, userId],
  );

  const handleViewHistoryItem = useCallback(
    async (row: any) => {
      const result = await refetchProtectedDataHistItem({
        params: { userid: userId, id: row.id, token },
      });
      if (!result) return;
      const data = result?.data;
      setViewData({
        dob: data.dob ?? "",
        address: data.address ?? "",
        nextOfKin: data.nextOfKin ?? "",
      });
      openView();
    },
    [refetchProtectedDataHistItem, userId, token, openView],
  );

  const handleAuthSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      await authPost({
        request: "auth-protected-user-data",
        body: { params: { pass, pin, userid: userId } },
        onSuccess: async result => {
          setToken(result.token);
          setPass("");
          setPin("");
          close();
          await fetchProtectedData(result.token);
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as before; fetchProtectedData already changes with userId
    [authPost, pass, pin, close, fetchProtectedData],
  );

  const handleSave = useCallback(async () => {
    await savePost({
      request: "save-protected-user-data",
      body: { params: { userid: userId, token, data } },
      onSuccess: () => {
        Notification(
          "Success",
          "Protected info has been successfully updated.",
          "green",
        );
      },
      onError: () => {
        relock();
      },
    });
  }, [savePost, userId, token, data, relock]);

  return (
    <Fragment>
      <Grid gutter="sm">
        <Col>
          <Text fz={"sm"}>Date of Birth</Text>
          <DateInput
            w={"50%"}
            value={toMantineDateString(data.dob)}
            onChange={value => setData(prev => ({ ...prev, dob: value ?? "" }))}
            valueFormat="DD MMM YYYY"
            readOnly={!unlocked || disabled}
            disabled={!unlocked}
          />
        </Col>

        <Col>
          <Text fz={"sm"}>Address</Text>
          <Input
            w={"50%"}
            value={data.address ?? ""}
            onChange={e =>
              setData(prev => ({ ...prev, address: e.target.value }))
            }
            readOnly={!unlocked || disabled}
            disabled={!unlocked}
          />
        </Col>

        <Col>
          <Text fz={"sm"}>Next of Kin</Text>
          <Input
            w={"50%"}
            value={data.nextOfKin ?? ""}
            onChange={e =>
              setData(prev => ({ ...prev, nextOfKin: e.target.value }))
            }
            readOnly={!unlocked || disabled}
            disabled={!unlocked}
          />
        </Col>

        <Grid.Col>
          <Row style={{ gap: 12 }}>
            <Button
              variant="outline"
              size="xs"
              leftSection={
                unlocked ? (
                  <IconLockOpen size={"1rem"} />
                ) : (
                  <IconLock size={"1rem"} />
                )
              }
              onClick={unlocked ? relock : open}>
              {unlocked ? "Lock" : "Unlock"}
            </Button>
            <Row></Row>
            {unlocked && (
              <Button
                variant="outline"
                size="xs"
                leftSection={<IconHistory size={"1rem"} />}
                onClick={openHistory}>
                History
              </Button>
            )}
            {unlocked && !disabled && (
              <Button
                size="xs"
                loading={isSaveLoading}
                leftSection={<IconDeviceFloppy size={"1rem"} />}
                onClick={handleSave}>
                Save
              </Button>
            )}
          </Row>
        </Grid.Col>
      </Grid>

      <ApiHistoryModal
        opened={historyOpened}
        close={closeHistory}
        elementName="Protected Info"
        apiUrl="get-protected-user-data-hist"
        params={{ userid: userId, token }}
        onView={handleViewHistoryItem}
      />
      <Modal
        size="lg"
        opened={viewOpened}
        onClose={closeView}
        title="Protected Info History">
        <Grid gutter="sm">
          <Col>
            <Text fz={"sm"}>Date of Birth</Text>
            <DateInput
              w={"50%"}
              value={toMantineDateString(viewData.dob)}
              valueFormat="DD MMM YYYY"
              readOnly
            />
          </Col>

          <Col>
            <Text fz={"sm"}>Address</Text>
            <Input w={"50%"} value={viewData.address ?? ""} readOnly />
          </Col>

          <Col>
            <Text fz={"sm"}>Next of Kin</Text>
            <Input w={"50%"} value={viewData.nextOfKin ?? ""} readOnly />
          </Col>
        </Grid>
      </Modal>

      <Modal
        size="lg"
        opened={opened}
        onClose={close}
        withCloseButton={false}
        yOffset={"25dvh"}>
        <Text>Authorization is required!</Text>
        <SmallDivider />
        <form onSubmit={handleAuthSubmit}>
          <FormsContainer>
            <RowsContainer style={{ width: "70%" }}>
              <Row>
                <Text style={{ marginRight: "10px", width: "92px" }}>
                  Password
                </Text>
                <PasswordInput
                  w={"80%"}
                  placeholder="Password"
                  value={pass}
                  onChange={e => setPass(e.target.value)}
                />
              </Row>
              <Row>
                <Text>PIN</Text>
                <PinInput value={pin} onChange={setPin} />
              </Row>
            </RowsContainer>
            <ButtonRow>
              <Button
                size="xs"
                leftSection={<IconX size={"1rem"} />}
                mr={5}
                color="red"
                onClick={close}>
                Cancel
              </Button>
              <Button
                loading={isAuthLoading}
                type="submit"
                size="xs"
                leftSection={<IconDeviceFloppy size={"1rem"} />}
                disabled={!pass && pin.length !== 4}
                ml={5}>
                Submit
              </Button>
            </ButtonRow>
          </FormsContainer>
        </form>
      </Modal>
    </Fragment>
  );
};

export default ProtectedInfo;
